import type { Carer, Resident, RiskAssessment } from '@lmc/ts-common'
import useFeatureFlags from 'hooks/useFeatureFlags'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { getNominative } from '../../../../../common/utils'
import { NAMESPACE } from '../../../../../locale'
import { riskAssessmentsConstants } from '../../../../../locale/riskAssessments'
import RiskAssessmentsSection from './RiskAssessmentsSection'

interface Params {
  risk_assessment_id?: string
}

interface RiskAssessmentsSectionsProps {
  carers?: Carer[]
  sortType?: string
  carePlanNominative?: string
  params: Params
  riskAssessments: RiskAssessment[]
  loading?: boolean
  onRiskAssessmentClick: (id: string) => void
  resident: Resident
  setSortType: (sortType: string) => void
}

interface SectionConfig {
  title: string
  emptyStateConfig: {
    helpMessage: string
    supportArticle: string
  }
}

interface RiskAssessmentsSectionsRendererProps {
  carers?: Carer[]
  sortType?: string
  carePlanNominative?: string
  params: Params
  loading?: boolean
  onRiskAssessmentClick: (id: string) => void
  setSortType: (sortType: string) => void
  currentRiskAssessments: RiskAssessment[]
  historicalRiskAssessments: RiskAssessment[]
  archivedRiskAssessments: RiskAssessment[]
  currentSectionConfig: SectionConfig
  historicalSectionConfig: SectionConfig
  archivedSectionConfig: SectionConfig
  riskArchivingFlagEnabled: boolean
}

const NO_HISTORICAL_ASSESSMENTS_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3353673-how-can-i-view-older-versions-of-a-risk-assessment'

const NO_UNLINKED_ASSESSMENTS_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3371462-how-do-i-remove-a-risk-assessment-from-a-care-plan'

const EMPTY_DASHBOARD_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3371486-risk-assessment-dashboard'

const RiskAssessmentsSectionsRenderer: React.FC<
  RiskAssessmentsSectionsRendererProps
> = ({
  carers,
  sortType,
  carePlanNominative,
  params,
  loading,
  onRiskAssessmentClick,
  setSortType,
  currentRiskAssessments,
  historicalRiskAssessments,
  archivedRiskAssessments,
  currentSectionConfig,
  historicalSectionConfig,
  archivedSectionConfig,
  riskArchivingFlagEnabled
}) => {
  return (
    <>
      <RiskAssessmentsSection
        title={currentSectionConfig.title}
        carers={carers}
        sortType={sortType}
        carePlanNominative={carePlanNominative}
        params={params}
        riskAssessments={currentRiskAssessments}
        loading={loading}
        onRiskAssessmentClick={onRiskAssessmentClick}
        setSortType={setSortType}
        emptyStateConfig={currentSectionConfig.emptyStateConfig}
      />
      <RiskAssessmentsSection
        title={historicalSectionConfig.title}
        carers={carers}
        sortType={sortType}
        carePlanNominative={carePlanNominative}
        params={params}
        riskAssessments={historicalRiskAssessments}
        loading={loading}
        onRiskAssessmentClick={onRiskAssessmentClick}
        setSortType={setSortType}
        emptyStateConfig={historicalSectionConfig.emptyStateConfig}
      />
      {riskArchivingFlagEnabled && (
        <RiskAssessmentsSection
          title={archivedSectionConfig.title}
          carers={carers}
          sortType={sortType}
          carePlanNominative={carePlanNominative}
          params={params}
          riskAssessments={archivedRiskAssessments}
          loading={loading}
          onRiskAssessmentClick={onRiskAssessmentClick}
          setSortType={setSortType}
          initialExpanded={false}
          emptyStateConfig={archivedSectionConfig.emptyStateConfig}
        />
      )}
    </>
  )
}

// Container component with logic
const RiskAssessmentsSections: React.FC<RiskAssessmentsSectionsProps> = ({
  carers,
  sortType,
  carePlanNominative,
  params,
  riskAssessments,
  loading,
  onRiskAssessmentClick,
  resident,
  setSortType
}) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS)
  const riskArchivingFlagEnabled = useFeatureFlags({
    flagNames: ['risk_assessments_archiving']
  })?.data.risk_assessments_archiving.enabled

  const filterCurrentRiskAssessments = (assessments: RiskAssessment[]) => {
    return assessments.filter(
      (assessment) => assessment.hasCarePlans && !assessment.archived
    )
  }

  const filterHistoricalRiskAssessments = (assessments: RiskAssessment[]) => {
    return assessments.filter(
      (assessment) => !assessment.hasCarePlans && !assessment.archived
    )
  }

  const filterArchivedRiskAssessments = (assessments: RiskAssessment[]) => {
    return assessments.filter((assessment) => assessment.archived)
  }

  const getCurrentSectionConfig = (): SectionConfig => {
    return {
      title: t(riskAssessmentsConstants.CURRENT),
      emptyStateConfig: {
        helpMessage: t(riskAssessmentsConstants.NO_CURRENT_RISK_ASSESSMENTS),
        supportArticle: EMPTY_DASHBOARD_ARTICLE
      }
    }
  }

  const getHistoricalSectionConfig = (): SectionConfig => {
    const isHistoryView = !!params.risk_assessment_id

    const title = isHistoryView
      ? t(riskAssessmentsConstants.HISTORY)
      : t(riskAssessmentsConstants.NOT_ATTACHED_TO_CARE_PLAN, {
          carePlanNominative: getNominative(
            carePlanNominative || '',
            'Care Plan'
          )
        })

    const helpMessage = isHistoryView
      ? t(riskAssessmentsConstants.NO_HISTORICAL_ASSESSMENTS)
      : t(riskAssessmentsConstants.NO_LINKED_ASSESSMENTS, {
          name: resident.name.first,
          carePlanNominative: getNominative(
            carePlanNominative || '',
            'Care Plans'
          )
        })

    const supportArticle = isHistoryView
      ? NO_HISTORICAL_ASSESSMENTS_ARTICLE
      : NO_UNLINKED_ASSESSMENTS_ARTICLE

    return {
      title,
      emptyStateConfig: {
        helpMessage,
        supportArticle
      }
    }
  }

  const getArchivedSectionConfig = (): SectionConfig => {
    return {
      title: t(riskAssessmentsConstants.ARCHIVED_RISK_ASSESSMENTS),
      emptyStateConfig: {
        helpMessage: t(riskAssessmentsConstants.NO_ARCHIVED_RISK_ASSESSMENTS),
        supportArticle: EMPTY_DASHBOARD_ARTICLE
      }
    }
  }

  const currentRiskAssessments = filterCurrentRiskAssessments(riskAssessments)
  const historicalRiskAssessments =
    filterHistoricalRiskAssessments(riskAssessments)
  const archivedRiskAssessments = filterArchivedRiskAssessments(riskAssessments)

  const currentSectionConfig = getCurrentSectionConfig()
  const historicalSectionConfig = getHistoricalSectionConfig()
  const archivedSectionConfig = getArchivedSectionConfig()

  return (
    <RiskAssessmentsSectionsRenderer
      carers={carers}
      sortType={sortType}
      carePlanNominative={carePlanNominative}
      params={params}
      loading={loading}
      onRiskAssessmentClick={onRiskAssessmentClick}
      setSortType={setSortType}
      currentRiskAssessments={currentRiskAssessments}
      historicalRiskAssessments={historicalRiskAssessments}
      archivedRiskAssessments={archivedRiskAssessments}
      currentSectionConfig={currentSectionConfig}
      historicalSectionConfig={historicalSectionConfig}
      archivedSectionConfig={archivedSectionConfig}
      riskArchivingFlagEnabled={riskArchivingFlagEnabled}
    />
  )
}

export default RiskAssessmentsSections
export { RiskAssessmentsSectionsRenderer }
